import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation, useLazyQuery, NetworkStatus } from '@apollo/client';
import { State, City } from 'country-state-city';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatPhoneForAPI } from '../utils/phoneUtils';
import { DEFAULTS } from '../config';
import StaffCreation from './StaffCreation';
import SubscriptionDetails from './SubscriptionDetails';
import 'react-phone-number-input/style.css';
import './PhoneInput.css';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const GET_USERS = gql`
  query GetUsers {
    users {
      id
      username
      email
      mobile
      role
      firstName
      lastName
      ownerName
      companyName
      businessIndustry
      name
      phone_number
      email_address
      address_line_1
      address_line_2
      city
      state
      country
      zip_code
      assigned_admin_id
      is_active
      notes
      email_verified
      email_verified_at
      created_at
      updated_at
    }
    userAdmins {
      id
      username
      name
      firstName
      lastName
    }
  }
`;

const GET_USER_SUBSCRIPTION = gql`
  query GetUserSubscription {
    mySubscriptions {
      id
      status
      starts_at
      expires_at
      package {
        id
        name
        max_staff_members
        max_contacts
        max_whatsapp_instances
        max_templates
        max_bulk_messages_per_day
        duration_days
        package_type
        price
        currency
      }
    }
  }
`;

const GET_ALL_SUBSCRIPTIONS = gql`
  query GetAllSubscriptions {
    allSubscriptions {
      id
      status
      starts_at
      expires_at
      user {
        id
        username
        email
        role
      }
      package {
        id
        name
        price
        currency
      }
      order {
        id
        amount
        status
        created_at
      }
    }
  }
`;

const GET_USER_STAFF = gql`
  query GetUserStaff($adminId: ID!) {
    userStaff(adminId: $adminId) {
      id
      username
      email
      mobile
      role
      name
      is_active
      notes
      created_at
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      username
      email
      mobile
      role
      name
      is_active
      created_at
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      username
      email
      mobile
      role
      name
      is_active
      notes
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

const TOGGLE_USER_STATUS = gql`
  mutation ToggleUserStatus($id: ID!, $notes: String) {
    toggleUserStatus(id: $id, notes: $notes) {
      id
      is_active
      notes
    }
  }
`;

const UserManagement = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showStaffCreation, setShowStaffCreation] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userId: null, userName: '' });
  const [statusToggle, setStatusToggle] = useState({ show: false, userId: null, currentStatus: false, userName: '', notes: '' });
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [currentView, setCurrentView] = useState('users'); // 'users' or 'subscriptions'
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showPackageInfoModal, setShowPackageInfoModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Location data for dropdowns
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [staffMembers, setStaffMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  // Debounce search term to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset page when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  // Form data for Create User
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    mobile: '',
    role: 'user_staff',
    firstName: '',
    lastName: '',
    ownerName: '',
    companyName: '',
    businessIndustry: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    country: 'IN', // Default to India country code
    zip_code: '',
    assigned_admin_id: ''
  });

  // Add escape key handler for modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showStaffModal) {
        console.log('Escape key pressed - closing modal'); // Debug log
        setShowStaffModal(false);
      }
    };

    if (showStaffModal) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showStaffModal]);

  // Load states when country changes
  useEffect(() => {
    if (editFormData.country) {
      const stateList = State.getStatesOfCountry(editFormData.country);
      setStates(stateList);
      setCities([]); // Clear cities when country changes
    }
  }, [editFormData.country]);

  // Load cities when state changes
  useEffect(() => {
    if (editFormData.country && editFormData.state) {
      const cityList = City.getCitiesOfState(editFormData.country, editFormData.state);
      setCities(cityList);
    }
  }, [editFormData.country, editFormData.state]);

  // Load states and cities for Create User form
  useEffect(() => {
    if (formData.country) {
      const stateList = State.getStatesOfCountry(formData.country);
      setStates(stateList);
      setCities([]); // Clear cities when country changes
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.country && formData.state) {
      const cityList = City.getCitiesOfState(formData.country, formData.state);
      setCities(cityList);
      setFormData(prev => ({ ...prev, city: '' }));
    }
  }, [formData.country, formData.state]);

  // Update form data when editingUser changes
  useEffect(() => {
    if (editingUser && showEditForm) {
      console.log('🔄 Updating form data for user:', editingUser);
      const detectedCountry = getCountryFromMobile(editingUser.mobile);

      const formData = {
        username: editingUser.username || '',
        firstName: editingUser.firstName || '',
        lastName: editingUser.lastName || '',
        email: editingUser.email || '',
        mobile: editingUser.mobile || '',
        ownerName: editingUser.ownerName || '',
        companyName: editingUser.companyName || '',
        businessIndustry: editingUser.businessIndustry || '',
        address_line_1: editingUser.address_line_1 || '',
        address_line_2: editingUser.address_line_2 || '',
        city: editingUser.city || '',
        state: editingUser.state || '',
        country: editingUser.country || detectedCountry,
        zip_code: editingUser.zip_code || '',
        role: editingUser.role || 'user_staff',
        notes: editingUser.notes || ''
      };

      console.log('📝 Form data being set:', formData);
      setEditFormData(formData);
    }
  }, [editingUser, showEditForm]);

  // Auto-detect country from mobile number
  const getCountryFromMobile = (mobile) => {
    if (!mobile) return 'IN'; // Default to India

    const cleanMobile = mobile.replace(/\D/g, '');

    if (cleanMobile.startsWith('91')) return 'IN'; // India
    if (cleanMobile.startsWith('1')) return 'US'; // USA/Canada
    if (cleanMobile.startsWith('44')) return 'GB'; // UK
    if (cleanMobile.startsWith('61')) return 'AU'; // Australia
    if (cleanMobile.startsWith('33')) return 'FR'; // France
    if (cleanMobile.startsWith('49')) return 'DE'; // Germany
    if (cleanMobile.startsWith('81')) return 'JP'; // Japan
    if (cleanMobile.startsWith('86')) return 'CN'; // China

    return 'IN'; // Default to India
  };

  // Validate phone number
  const validatePhone = (phone) => {
    if (!phone) {
      return false;
    }

    if (!isValidPhoneNumber(phone)) {
      return false;
    }

    return true;
  };

  const { data: subscriptionData } = useQuery(GET_USER_SUBSCRIPTION, {
    skip: currentUser?.role !== 'user_admin',
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_USERS, {
    fetchPolicy: 'network-only', // Always fetch fresh data
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      console.log('Users Query Completed:', data);
    },
    onError: (error) => {
      console.error('Users Query Error:', error);
    }
  });

  const { data: allSubscriptionsData, loading: allSubscriptionsLoading, error: allSubscriptionsError } = useQuery(GET_ALL_SUBSCRIPTIONS, {
    // Always load subscription data to check for user deletion constraints
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: (data) => {
      console.log('✅ User deleted successfully:', data);
    },
    onError: (error) => {
      console.error('❌ Error deleting user:', error);
    }
  });
  const [toggleUserStatus] = useMutation(TOGGLE_USER_STATUS);

  // eslint-disable-next-line no-unused-vars
  const [getStaffMembers, { loading: staffLoading }] = useLazyQuery(GET_USER_STAFF);

  // Helper functions for package restrictions
  const getCurrentSubscription = () => {
    if (!subscriptionData?.mySubscriptions) return null;
    return subscriptionData.mySubscriptions.find(sub => sub.status === 'active');
  };

  const getStaffLimits = () => {
    const subscription = getCurrentSubscription();
    if (!subscription) return { current: 0, max: 0, packageName: 'No Package' };

    const currentStaffCount = filteredUsers.length;
    const maxStaff = subscription.package?.max_staff_members || 0;
    const packageName = subscription.package?.name || 'Unknown Package';

    return { current: currentStaffCount, max: maxStaff, packageName };
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      mobile: '',
      role: 'user_staff',
      firstName: '',
      lastName: '',
      ownerName: '',
      companyName: '',
      businessIndustry: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      country: 'IN', // Default to India country code
      zip_code: '',
      assigned_admin_id: ''
    });
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number
    if (!validatePhone(formData.mobile)) {
      return;
    }

    try {
      if (editingUser) {
        await updateUser({
          variables: {
            id: editingUser.id,
            input: formData
          }
        });
      } else {
        // Format phone for API and create user data
        const formattedPhone = formatPhoneForAPI(formData.mobile);
        const createUserData = {
          ...formData,
          mobile: formattedPhone,
          password: 'defaultPassword123' // Set a default password since it's required by backend
        };

        await createUser({
          variables: { input: createUserData }
        });
      }
      resetForm();
      refetch();
    } catch (err) {
      console.error('Error saving user:', err);
      // Show error in a proper way instead of alert
    }
  };

  const handleEdit = (user) => {
    console.log('🔍 Editing user data:', user); // Debug log
    setEditingUser(user);
    const detectedCountry = getCountryFromMobile(user.mobile);

    const formData = {
      username: user.username || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      mobile: user.mobile || '',
      ownerName: user.ownerName || '',
      companyName: user.companyName || '',
      businessIndustry: user.businessIndustry || '',
      address_line_1: user.address_line_1 || '',
      address_line_2: user.address_line_2 || '',
      city: user.city || '',
      state: user.state || '',
      country: user.country || detectedCountry,
      zip_code: user.zip_code || '',
      role: user.role || 'user_staff',
      notes: user.notes || ''
    };

    console.log('📝 Setting form data:', formData); // Debug log
    setEditFormData(formData);
    setShowEditForm(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser({
        variables: {
          id: editingUser.id,
          input: editFormData
        }
      });

      setShowEditForm(false);
      setEditingUser(null);
      setEditFormData({});
      refetch();
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleEditFormCancel = () => {
    setShowEditForm(false);
    setEditingUser(null);
    setEditFormData({});
  };

  // Helper function to check if user has active subscriptions
  const userHasActiveSubscriptions = (userId) => {
    if (!allSubscriptionsData?.allSubscriptions) return false;
    
    return allSubscriptionsData.allSubscriptions.some(subscription => 
      subscription.user?.id === userId && subscription.status === 'active'
    );
  };

  const handleDelete = async (userId) => {
    const user = data?.users?.find(u => u.id === userId);
    
    // Check if user still exists (prevent deleting already deleted users)
    if (!user) {
      setErrorMessage('User no longer exists. The list will be refreshed.');
      setShowErrorModal(true);
      // Refresh the data to sync with database
      refetch();
      return;
    }
    
    // Check if user has active subscriptions
    if (userHasActiveSubscriptions(userId)) {
      // Show error message for users with active subscriptions
      setErrorMessage('Cannot delete user with active subscriptions. Please cancel their subscription first.');
      setShowErrorModal(true);
      return;
    }
    
    setDeleteConfirm({ show: true, userId, userName: user?.username || 'Unknown User' });
  };

  const confirmDelete = async () => {
    if (isDeleting) return; // Prevent multiple delete attempts
    
    const userIdToDelete = deleteConfirm.userId;
    const userNameToDelete = deleteConfirm.userName;
    
    setIsDeleting(true);
    
    try {
      const { data } = await deleteUser({ 
        variables: { id: userIdToDelete },
        // Update cache to remove the deleted user
        update(cache, { data }) {
          if (data?.deleteUser) {
            try {
              // Remove the deleted user from the cache
              const existingUsers = cache.readQuery({ query: GET_USERS });
              if (existingUsers) {
                cache.writeQuery({
                  query: GET_USERS,
                  data: {
                    ...existingUsers,
                    users: existingUsers.users.filter(user => user.id !== userIdToDelete)
                  }
                });
                console.log('✅ User removed from cache successfully');
              }
            } catch (cacheError) {
              console.warn('⚠️ Cache update failed, will refetch:', cacheError);
              // Fallback to refetch if cache update fails
              refetch();
            }
          }
        },
        // Optimistic update - immediately remove from UI
        optimisticResponse: {
          deleteUser: true
        }
      });
      
      if (data?.deleteUser) {
        console.log('✅ User successfully deleted from database');
        setDeleteConfirm({ show: false, userId: null, userName: '' });
        
        // Show success message
        setSuccessMessage(`User "${userNameToDelete}" has been successfully deleted.`);
        setShowSuccessModal(true);
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('❌ Error deleting user:', err);
      setDeleteConfirm({ show: false, userId: null, userName: '' });
      
      // Show proper error message
      if (err.message?.includes('already deleted') || err.message?.includes('not found')) {
        setErrorMessage('User has already been deleted. Refreshing the list...');
        // Refresh to sync with database
        setTimeout(() => refetch(), 1000);
      } else {
        setErrorMessage(err.message || 'Failed to delete user. Please try again.');
      }
      setShowErrorModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const user = data?.users?.find(u => u.id === userId);
    setStatusToggle({
      show: true,
      userId,
      currentStatus,
      userName: user?.username || 'Unknown User',
      notes: ''
    });
  };

  const confirmStatusToggle = async () => {
    const userIdToToggle = statusToggle.userId;
    const userNameToToggle = statusToggle.userName;
    const newStatus = !statusToggle.currentStatus;
    
    try {
      const { data } = await toggleUserStatus({
        variables: {
          id: userIdToToggle,
          notes: statusToggle.notes || `Status changed to ${newStatus ? 'active' : 'inactive'} on ${new Date().toLocaleString()}`
        },
        // Update cache to reflect the status change
        update(cache, { data }) {
          if (data?.toggleUserStatus) {
            try {
              const existingUsers = cache.readQuery({ query: GET_USERS });
              if (existingUsers) {
                cache.writeQuery({
                  query: GET_USERS,
                  data: {
                    ...existingUsers,
                    users: existingUsers.users.map(user => 
                      user.id === userIdToToggle 
                        ? { ...user, is_active: data.toggleUserStatus.is_active, notes: data.toggleUserStatus.notes }
                        : user
                    )
                  }
                });
                console.log('✅ User status updated in cache successfully');
              }
            } catch (cacheError) {
              console.warn('⚠️ Cache update failed, will refetch:', cacheError);
              refetch();
            }
          }
        }
      });
      
      if (data?.toggleUserStatus) {
        console.log('✅ User status toggled successfully');
        setStatusToggle({ show: false, userId: null, currentStatus: false, userName: '', notes: '' });
        
        // Show success message
        const statusText = data.toggleUserStatus.is_active ? 'activated' : 'deactivated';
        setSuccessMessage(`User "${userNameToToggle}" has been ${statusText} successfully.`);
        setShowSuccessModal(true);
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('❌ Error toggling user status:', err);
      setStatusToggle({ show: false, userId: null, currentStatus: false, userName: '', notes: '' });
      
      // Show proper error message
      setErrorMessage(err.message || 'Failed to update user status. Please try again.');
      setShowErrorModal(true);
    }
  };

  const filteredUsers = data?.users?.filter(user => {
    // Role-based filtering
    if (currentUser?.role === 'user_admin') {
      // user_admin should only see their staff members (user_staff assigned to them)
      const isStaffMember = user.role === 'user_staff' && user.assigned_admin_id === currentUser.id;
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesSearch = !debouncedSearchTerm ||
        user.username?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (user.name && user.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        user.mobile?.includes(debouncedSearchTerm) ||
        `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      return isStaffMember && matchesRole && matchesSearch;
    } else {
      // super_admin sees user_admin and user_staff, exclude super_admin
      const isValidRole = user.role === 'user_admin' || user.role === 'user_staff';
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesSearch = !debouncedSearchTerm ||
        user.username?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (user.name && user.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        user.mobile?.includes(debouncedSearchTerm) ||
        `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      return isValidRole && matchesRole && matchesSearch;
    }
  }) || [];

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Search suggestions
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching

    if (value.length > 0) {
      const suggestions = data?.users?.filter(user => {
        // Role-based filtering for suggestions
        if (currentUser?.role === 'user_admin') {
          // user_admin should only see their staff members
          const isStaffMember = user.role === 'user_staff' && user.assigned_admin_id === currentUser.id;
          return isStaffMember && (
            user.username?.toLowerCase().includes(value.toLowerCase()) ||
            user.name?.toLowerCase().includes(value.toLowerCase()) ||
            `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(value.toLowerCase())
          );
        } else {
          // super_admin sees user_admin and user_staff
          const isValidRole = user.role === 'user_admin' || user.role === 'user_staff';
          return isValidRole && (
            user.username?.toLowerCase().includes(value.toLowerCase()) ||
            user.name?.toLowerCase().includes(value.toLowerCase()) ||
            `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(value.toLowerCase())
          );
        }
      }).slice(0, 5).map(user => ({
        id: user.id,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username,
        username: user.username,
        email: user.email
      })) || [];

      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      console.error('Error refreshing users:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const handleStaffCreated = (newStaff) => {
    console.log('✅ Staff member created:', newStaff);
    // Refresh the users list
    refetch();
  };

  // Debug logging
  useEffect(() => {
    if (data?.users) {
      console.log('Users data received:', data.users);
      data.users.forEach(user => {
        console.log(`User ${user.username}: created_at = ${user.created_at}, type = ${typeof user.created_at}`);
      });
    }
  }, [data]);

  // Determine different loading states
  const isInitialLoading = loading && !data;
  const isDataRefreshing = loading && data && networkStatus === NetworkStatus.refetch;
  const isSearching = loading && data && (networkStatus === NetworkStatus.setVariables || networkStatus === NetworkStatus.loading);
  const isTypingSearch = searchTerm !== debouncedSearchTerm; // User is typing but debounce hasn't triggered yet
  const isDataOperationInProgress = isDataRefreshing || isSearching || isRefreshing || isTypingSearch;
  
  if (isInitialLoading) return <div className="p-8 text-center text-gray-600">Loading users...</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error.message}</div>;

  return (
    <div className="p-4 md:p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6 overflow-hidden relative">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 relative z-10">
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            {/* View Toggle Buttons */}
            {currentUser?.role === 'super_admin' && (
              <div className="flex bg-gray-50 p-1 rounded-lg border border-gray-100">
                <button
                  onClick={() => setCurrentView('users')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-normal uppercase tracking-wider transition-all ${currentView === 'users'
                    ? 'bg-white text-bulky-navy-800 shadow-sm border border-gray-100'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  Identities
                </button>
                <button
                  onClick={() => setCurrentView('subscriptions')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-normal uppercase tracking-wider transition-all ${currentView === 'subscriptions'
                    ? 'bg-white text-bulky-navy-800 shadow-sm border border-gray-100'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  Packages
                </button>
              </div>
            )}

            {currentView === 'users' && (
              <div className="flex gap-2 w-full sm:w-auto">
                {currentUser?.role === 'super_admin' ? (
                  <button
                    className="flex-1 sm:flex-none justify-center bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-4 py-2.5 rounded-xl transition-all duration-300 shadow-md shadow-bulky-blue-500/10 active:scale-95 flex items-center gap-2 text-[10px] uppercase tracking-wider"
                    onClick={() => setShowCreateForm(true)}
                  >
                    <span className="material-icons-round text-sm">person_add</span>
                    <span>Add New User</span>
                  </button>
                ) : currentUser?.role === 'user_admin' ? (
                  <button
                    className="flex-1 sm:flex-none justify-center bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-4 py-2.5 rounded-xl transition-all duration-300 shadow-md shadow-bulky-blue-500/10 active:scale-95 flex items-center gap-2 text-[10px] uppercase tracking-wider"
                    onClick={() => setShowStaffCreation(true)}
                  >
                    <span className="material-icons-round text-sm">group_add</span>
                    <span>Add Staff</span>
                  </button>
                ) : null}
              </div>
            )}

            {/* Global Actions */}
            <div className="flex gap-2">
              {currentUser?.role === 'user_admin' && (
                <button
                  className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-normal px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 text-[9px] uppercase tracking-wider"
                  onClick={() => setShowPackageInfoModal(true)}
                >
                  <span className="material-icons-round text-sm">info</span>
                  <span>Limits</span>
                </button>
              )}

              <button
                className="bg-gray-50 hover:bg-gray-100 text-gray-500 font-normal px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 disabled:opacity-50 text-[9px] uppercase tracking-wider"
                onClick={handleRefresh}
                disabled={isDataOperationInProgress}
              >
                <span className={`material-icons-round text-sm ${isDataOperationInProgress ? 'animate-spin' : ''}`}>refresh</span>
                <span>{isDataOperationInProgress ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6 bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-900 p-3 px-5 rounded-xl border border-white/5 shadow-inner">
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">Total Users</span>
              <span className="text-sm font-normal text-white leading-none tabular-nums">{filteredUsers.length}</span>
            </div>
            <div className="w-px h-6 bg-white/10"></div>
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">Active Users</span>
              <span className="text-sm font-normal text-emerald-400 leading-none tabular-nums">{filteredUsers.filter(u => u.is_active).length}</span>
            </div>
            <div className="w-px h-6 bg-white/10"></div>
            <div className="flex flex-col">
              <span className="text-[7px] font-normal text-bulky-navy-300 uppercase tracking-wider leading-none mb-1">System Health</span>
              <span className="text-sm font-normal text-blue-400 leading-none tabular-nums">Stable</span>
            </div>
          </div>
        </div>

        {/* Global Controls Row */}
        <div className="mt-5 pt-5 border-t border-gray-50 flex flex-col sm:flex-row gap-3">
          <div className="relative group flex-1 sm:w-48">
            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 pointer-events-none text-lg">filter_list</span>
            <select
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select pl-12 h-[46px]"
              disabled={isDataOperationInProgress}
            >
              <option value="all">All Users</option>
              <option value="user_admin">Admin Users</option>
              <option value="user_staff">Staff Users</option>
            </select>
            <span className="material-icons-round absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">expand_more</span>
            {isDataOperationInProgress && (
              <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          <div className="relative group flex-[2]">
            <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors z-10 text-xl">search</span>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => searchTerm.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="form-input pl-12 h-[46px]"
              disabled={isDataOperationInProgress}
            />
            {isTypingSearch && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            {isDataOperationInProgress && searchTerm && !isTypingSearch && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Search Suggestions */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                {searchSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-bulky-blue-100 rounded-lg flex items-center justify-center text-bulky-blue-600 font-normal text-xs">
                        {suggestion.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-normal text-gray-900">{suggestion.name}</p>
                        <p className="text-xs text-gray-500">@{suggestion.username} • {suggestion.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateForm && createPortal(
        <div className="modal-overlay">
          <div className="modal-container max-w-2xl">
            <div className="modal-header bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                  <span className="material-icons-round text-2xl">person_add</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight leading-none">Add New User</h2>
                  <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Create a new user account</p>
                </div>
              </div>
              <button onClick={resetForm} className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg">
                <span className="material-icons-round text-2xl">close</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
              <div className="modal-content">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="form-input"
                      placeholder="Set username..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input"
                      placeholder="Enter email address..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="form-label">Phone Number</label>
                    <PhoneInput
                      international
                      defaultCountry={DEFAULTS.COUNTRY}
                      value={formData.mobile}
                      onChange={(v) => setFormData({ ...formData, mobile: v || '' })}
                      className="phone-input tech-input"
                    />
                  </div>
                  <div className="space-y-1.5 relative">
                    <label className="form-label">User Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="form-select"
                    >
                      <option value="user_admin">User Admin</option>
                      <option value="user_staff">User Staff</option>
                    </select>
                    <span className="material-icons-round absolute right-4 top-[38px] text-gray-400 pointer-events-none text-base">expand_more</span>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={resetForm} className="btn-secondary">Discard</button>
                <button type="submit" className="btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
        {/* Loading Overlay for Data Operations - Only shows during data refresh, not initial load */}
        {isDataOperationInProgress && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-600 font-medium">
                {isTypingSearch ? 'Preparing search...' : isSearching ? 'Searching users...' : isRefreshing ? 'Refreshing data...' : 'Updating users...'}
              </p>
            </div>
          </div>
        )}

        {currentView === 'users' ? (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100/50 border-b border-gray-200 text-[12px] font-bold text-bulky-navy-800 uppercase tracking-widest">
                    <th className="px-8 py-6 text-left">User Profile</th>
                    <th className="px-8 py-6 text-left">Contact Information</th>
                    <th className="px-8 py-6 text-left">Role</th>
                    <th className="px-8 py-6 text-left">Status</th>
                    <th className="px-8 py-6 text-left">Created On</th>
                    <th className="px-8 py-6 text-right pr-12">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentUsers.map(user => (
                    <tr key={user.id} className={`group hover:bg-bulky-blue-50/30 transition-all duration-300 ${!user.is_active ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-bulky-navy-900 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg border-2 border-white group-hover:scale-105 transition-transform">
                            {(() => {
                              const displayName = user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username);
                              return displayName?.substring(0, 2).toUpperCase() || 'ID';
                            })()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[14px] font-bold text-bulky-navy-900 tracking-tight leading-none mb-2 truncate">
                              {user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username)}
                            </p>
                            <p className="text-[11px] font-bold text-gray-400 tracking-widest uppercase opacity-70">USER ID: {user.username || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 rounded-lg bg-bulky-blue-50 flex items-center justify-center">
                              <span className="material-icons-round text-sm text-bulky-blue-500">alternate_email</span>
                            </div>
                            <p className="text-[13px] font-semibold text-bulky-navy-800 tracking-tight">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center">
                              <span className="material-icons-round text-sm text-gray-400">phone</span>
                            </div>
                            <p className="text-[11px] font-bold text-gray-400 tracking-widest uppercase opacity-80">{user.mobile || 'NOT SET'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 ${user.role === 'user_admin'
                          ? 'bg-blue-50/50 text-blue-600 border-blue-100 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
                          : 'bg-gray-50/50 text-gray-500 border-gray-100'
                          }`}>
                          <span className="text-[11px] font-bold uppercase tracking-widest">{user.role?.replace('user_', '') || 'USER'}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-2">
                          <div className={`inline-flex items-center gap-2.5 px-4 py-1.5 rounded-xl border-2 ${user.is_active
                            ? 'bg-emerald-50/50 text-emerald-600 border-emerald-100 shadow-[0_0_15px_rgba(16,185,129,0.1)]'
                            : 'bg-red-50/50 text-red-600 border-red-100'
                            }`}>
                            <div className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-emerald-500 animate-pulse-slow shadow-[0_0_10px_rgba(16,185,129,0.6)]' : 'bg-red-500'}`}></div>
                            <span className="text-[11px] font-bold uppercase tracking-widest">{user.is_active ? 'Active' : 'Locked'}</span>
                          </div>
                          
                          {/* Hide Subscribed badge for super admin */}
                          {currentUser?.role !== 'super_admin' && userHasActiveSubscriptions(user.id) && (
                            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
                              <span className="material-icons-round text-xs">card_membership</span>
                              <span className="text-[9px] font-bold uppercase tracking-wider">Subscribed</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex flex-col gap-1">
                          <p className="text-[13px] font-bold text-bulky-navy-900 tracking-tight">
                            {user.created_at ? new Date(parseInt(user.created_at)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}
                          </p>
                          <p className="text-[10px] font-bold text-blue-500/60 tracking-widest uppercase">Verified</p>
                        </div>
                      </td>
                      <td className="px-6 py-2.5 text-right pr-6">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleEdit(user)}
                            className="w-10 h-10 bg-white border border-gray-200 hover:border-bulky-blue-500 hover:text-bulky-blue-500 rounded-xl flex items-center justify-center transition-all active:scale-90 shadow-sm"
                            title="Edit User"
                          >
                            <span className="material-icons-round text-lg">edit</span>
                          </button>

                          <button
                            onClick={() => handleToggleStatus(user.id, user.is_active)}
                            className={`w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center transition-all active:scale-90 shadow-sm ${user.is_active ? 'hover:border-yellow-500 hover:text-yellow-500' : 'hover:border-emerald-500 hover:text-emerald-500'}`}
                            title={user.is_active ? 'Suspend' : 'Resume'}
                          >
                            <span className="material-icons-round text-lg">{user.is_active ? 'block' : 'check_circle'}</span>
                          </button>

                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={userHasActiveSubscriptions(user.id)}
                            className={`w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center transition-all active:scale-90 shadow-sm ${
                              userHasActiveSubscriptions(user.id) 
                                ? 'opacity-50 cursor-not-allowed text-gray-400' 
                                : 'hover:border-red-500 hover:text-red-500'
                            }`}
                            title={userHasActiveSubscriptions(user.id) ? "Cannot delete user with active subscription" : "Delete"}
                          >
                            <span className="material-icons-round text-lg">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Grid */}
            <div className="md:hidden divide-y divide-gray-50">
              {currentUsers.map(user => (
                <div key={user.id} className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-bulky-navy-900 flex items-center justify-center text-white font-normal text-[9px] uppercase tracking-tighter shadow-sm">
                        {user.username?.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-tight leading-none mb-1">{user.name || user.username}</p>
                        <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider">ID: {user.id.substring(0, 6)}</p>
                      </div>
                    </div>
                    <div className={`px-2 py-0.5 rounded border ${user.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                      <span className="text-[7px] font-normal uppercase tracking-wider">{user.is_active ? 'ACTIVE' : 'LOCKED'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 bg-gray-50/50 p-2.5 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Contact</p>
                      <p className="text-[9px] font-normal text-bulky-navy-600 truncate">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-[7px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Access Tier</p>
                      <p className="text-[9px] font-normal text-bulky-navy-600 uppercase tracking-wider">{user.role?.replace('user_', '').toUpperCase()}</p>
                    </div>
                  </div>

                  <div className="flex gap-1.5 pt-1">
                    <button onClick={() => handleEdit(user)} className="flex-1 bg-white border border-gray-200 text-bulky-navy-400 py-2 rounded-md flex items-center justify-center active:scale-95 transition-all text-xs">
                      <span className="material-icons-round text-sm">tune</span>
                    </button>
                    <button onClick={() => handleToggleStatus(user.id, user.is_active)} className={`flex-1 border border-gray-200 py-2 rounded-md flex items-center justify-center active:scale-95 transition-all text-xs ${user.is_active ? 'bg-white text-yellow-500' : 'bg-white text-emerald-500'}`}>
                      <span className="material-icons-round text-sm">{user.is_active ? 'power_settings_new' : 'bolt'}</span>
                    </button>
                    <button 
                      onClick={() => handleDelete(user.id)} 
                      disabled={userHasActiveSubscriptions(user.id)}
                      className={`flex-1 bg-white border border-gray-200 py-2 rounded-md flex items-center justify-center active:scale-95 transition-all text-xs ${
                        userHasActiveSubscriptions(user.id) 
                          ? 'opacity-50 cursor-not-allowed text-gray-400' 
                          : 'text-red-500 hover:bg-red-50'
                      }`}
                      title={userHasActiveSubscriptions(user.id) ? "Cannot delete user with active subscription" : "Delete"}
                    >
                      <span className="material-icons-round text-sm">delete_sweep</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-50 bg-gray-50/30 relative">
                {isDataOperationInProgress && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] opacity-80">
                  <span>Showing Users</span>
                  <span className="text-bulky-navy-900">{indexOfFirstUser + 1}</span>
                  <span className="opacity-30">/</span>
                  <span className="text-bulky-navy-900">{Math.min(indexOfLastUser, filteredUsers.length)}</span>
                  <span className="opacity-30">of</span>
                  <span className="text-bulky-navy-900">{filteredUsers.length}</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1 || isDataOperationInProgress}
                    className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-100 text-gray-400 hover:text-bulky-blue-500 disabled:opacity-30 transition-all shadow-sm"
                  >
                    <span className="material-icons-round text-sm">chevron_left</span>
                  </button>

                  <div className="flex items-center gap-1 mx-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        disabled={isDataOperationInProgress}
                        className={`w-7 h-7 flex items-center justify-center text-[10px] font-normal rounded-lg transition-all disabled:opacity-50 ${currentPage === page
                          ? 'bg-bulky-navy-900 text-white shadow-md'
                          : 'bg-white text-gray-400 border border-gray-50 hover:border-gray-200'
                          }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || isDataOperationInProgress}
                    className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-100 text-gray-400 hover:text-bulky-blue-500 disabled:opacity-30 transition-all shadow-sm"
                  >
                    <span className="material-icons-round text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            )}

            {filteredUsers.length === 0 && (
              <div className="p-20 text-center bg-gray-50/50">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-gray-300">
                  <span className="material-icons-round text-4xl">person_search</span>
                </div>
                <p className="text-bulky-navy-300 font-normal tracking-tight uppercase text-xs">No users found matching your search.</p>
              </div>
            )}
          </>
        ) : (
          /* Subscriptions View */
          <>
            {allSubscriptionsLoading ? (
              <div className="p-20 text-center">
                <div className="w-12 h-12 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 font-normal">Loading subscriptions...</p>
              </div>
            ) : allSubscriptionsError ? (
              <div className="p-20 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-icons-round text-red-500 text-2xl">error</span>
                </div>
                <p className="text-red-600 font-normal">Error loading subscriptions</p>
                <p className="text-gray-500 text-sm mt-1">{allSubscriptionsError.message}</p>
              </div>
            ) : (
              <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100 uppercase tracking-wider text-[10px] font-normal text-bulky-navy-300">
                        <th className="px-8 py-6 text-left">User</th>
                        <th className="px-8 py-6 text-left">Package</th>
                        <th className="px-8 py-6 text-left">Status</th>
                        <th className="px-8 py-6 text-left">Duration</th>
                        <th className="px-8 py-6 text-left">Payment</th>
                        <th className="px-8 py-6 text-right pr-12">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {allSubscriptionsData?.allSubscriptions?.map(subscription => (
                        <tr key={subscription.id} className="group hover:bg-bulky-blue-50/20 transition-all duration-300">
                          <td className="compact-table-cell">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-bulky-navy-50 flex items-center justify-center text-bulky-navy-400 font-normal text-[10px] uppercase tracking-tighter shrink-0">
                                {(() => {
                                  const displayName = subscription.user?.name ||
                                    (subscription.user?.firstName && subscription.user?.lastName ?
                                      `${subscription.user.firstName} ${subscription.user.lastName}` :
                                      subscription.user?.firstName || subscription.user?.lastName || subscription.user?.username);
                                  return displayName?.substring(0, 2) || 'U';
                                })()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-normal text-bulky-navy-800 tracking-tight leading-none mb-1 truncate">
                                  {subscription.user?.name ||
                                    (subscription.user?.firstName && subscription.user?.lastName ?
                                      `${subscription.user.firstName} ${subscription.user.lastName}` :
                                      subscription.user?.firstName || subscription.user?.lastName || subscription.user?.username || 'Unknown User')}
                                </p>
                                <p className="text-[9px] font-normal text-gray-400 truncate uppercase tracking-wider">{subscription.user?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="compact-table-cell">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center shrink-0">
                                <span className="material-icons-round text-white text-[12px]">card_membership</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-normal text-bulky-navy-800 truncate">{subscription.package?.name}</p>
                                <p className="text-[10px] text-gray-400 font-normal">₹{subscription.package?.price}</p>
                              </div>
                            </div>
                          </td>
                          <td className="compact-table-cell text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider border ${subscription.status === 'active'
                              ? 'bg-green-50 text-green-600 border-green-100'
                              : 'bg-red-50 text-red-600 border-red-100'
                              }`}>
                              <div className={`w-1 h-1 rounded-full ${subscription.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                              {subscription.status}
                            </span>
                          </td>
                          <td className="compact-table-cell">
                            <div className="text-[10px] font-normal text-bulky-navy-800 leading-tight">
                              {new Date(subscription.starts_at).toLocaleDateString()} - {new Date(subscription.expires_at).toLocaleDateString()}
                              <p className="text-gray-400 font-normal mt-0.5 text-[9px] uppercase tracking-tighter">
                                {Math.ceil((new Date(subscription.expires_at) - new Date()) / (1000 * 60 * 60 * 24))} days left
                              </p>
                            </div>
                          </td>
                          <td className="compact-table-cell text-right">
                            <div className="flex justify-end gap-1.5">
                              <button
                                onClick={() => {
                                  setSelectedSubscription(subscription);
                                  setShowSubscriptionDetails(true);
                                }}
                                className="w-10 h-10 bg-white shadow-md shadow-bulky-navy-900/5 border border-gray-200 hover:border-bulky-blue-500 hover:text-bulky-blue-500 rounded-xl flex items-center justify-center transition-all active:scale-95"
                                title="View Details"
                              >
                                <span className="material-icons-round text-lg">visibility</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Subscription Cards */}
                <div className="md:hidden divide-y divide-gray-50">
                  {allSubscriptionsData?.allSubscriptions?.map(subscription => (
                    <div key={subscription.id} className="p-6 space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          <div className="w-10 h-10 rounded-xl bg-bulky-navy-50 flex items-center justify-center text-bulky-navy-400 font-normal text-xs uppercase">
                            {(() => {
                              const displayName = subscription.user?.name ||
                                (subscription.user?.firstName && subscription.user?.lastName ?
                                  `${subscription.user.firstName} ${subscription.user.lastName}` :
                                  subscription.user?.firstName || subscription.user?.lastName || subscription.user?.username);
                              return displayName?.substring(0, 2) || 'U';
                            })()}
                          </div>
                          <div>
                            <h2 className="font-medium">
                              {subscription.user?.name ||
                                (subscription.user?.firstName && subscription.user?.lastName ?
                                  `${subscription.user.firstName} ${subscription.user.lastName}` :
                                  subscription.user?.firstName || subscription.user?.lastName || subscription.user?.username)}
                            </h2>
                            <p className="text-[10px] text-gray-400 font-normal tracking-wider">{subscription.package?.name}</p>
                          </div>
                        </div>
                        <div className={`px-2 py-0.5 rounded-md text-[8px] font-normal uppercase tracking-wider ${subscription.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                          }`}>
                          {subscription.status}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-50">
                        <div>
                          <p className="text-[8px] font-normal text-gray-300 uppercase tracking-tighter">Package</p>
                          <p className="text-[10px] font-normal text-bulky-navy-800">₹{subscription.package?.price}</p>
                        </div>
                        <div>
                          <p className="text-[8px] font-normal text-gray-300 uppercase tracking-tighter">Expires</p>
                          <p className="text-[10px] font-normal text-bulky-navy-800">{new Date(subscription.expires_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowSubscriptionDetails(true);
                          }}
                          className="flex-1 bg-bulky-navy-50 text-bulky-navy-400 py-3 rounded-xl flex items-center justify-center"
                        >
                          <span className="material-icons-round text-base">visibility</span>
                        </button>
                        {subscription.status === 'active' && (
                          <button className="flex-1 bg-red-50 text-red-500 py-3 rounded-xl flex items-center justify-center">
                            <span className="material-icons-round text-base">cancel</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {(!allSubscriptionsData?.allSubscriptions || allSubscriptionsData.allSubscriptions.length === 0) && (
                  <div className="p-20 text-center bg-gray-50/50">
                    <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 text-gray-300">
                      <span className="material-icons-round text-4xl">card_membership</span>
                    </div>
                    <p className="text-bulky-navy-300 font-normal tracking-tight uppercase text-xs">No active subscriptions found.</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Staff Hierarchy Dialog */}
      {
        showStaffModal && createPortal(
          <div className="modal-overlay">
            <div className="modal-container max-w-4xl">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-white text-sm">corporate_fare</span>
                  </div>
                  <div>
                    <h2 className="font-medium">Staff Hierarchy</h2>
                  </div>
                </div>
                <button
                  onClick={() => setShowStaffModal(false)}
                  className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
                >
                  <span className="material-icons-round text-sm">close</span>
                </button>
              </div>

              {/* Content */}
              <div className="modal-content">
                {staffLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-bulky-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-xs font-normal text-gray-500 uppercase tracking-wider">Loading staff members...</p>
                  </div>
                ) : staffMembers.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                        <p className="text-lg font-normal text-blue-700 leading-none">{staffMembers.length}</p>
                        <p className="text-[10px] text-blue-600 font-normal uppercase tracking-wider mt-1">Total</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                        <p className="text-lg font-normal text-green-700 leading-none">{staffMembers.filter(s => s.is_active).length}</p>
                        <p className="text-[10px] text-green-600 font-normal uppercase tracking-wider mt-1">Active</p>
                      </div>
                      <div className="bg-bulky-navy-50 rounded-xl p-3 border border-bulky-navy-100">
                        <p className="text-lg font-normal text-bulky-navy-700 leading-none">{staffMembers.filter(s => s.role === 'user_admin').length}</p>
                        <p className="text-[10px] text-bulky-navy-600 font-normal uppercase tracking-wider mt-1">Admins</p>
                      </div>
                    </div>

                    {/* Staff List */}
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <div className="divide-y divide-gray-50">
                        {staffMembers.map((staff, index) => (
                          <div key={staff.id || index} className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-bulky-navy-500 rounded-lg flex items-center justify-center text-white font-normal text-xs">
                                  {(() => {
                                    const displayName = staff.name ||
                                      (staff.firstName && staff.lastName ?
                                        `${staff.firstName} ${staff.lastName}` :
                                        staff.firstName || staff.lastName || staff.username);
                                    return displayName?.charAt(0).toUpperCase() || 'U';
                                  })()}
                                </div>
                                <div>
                                  <h2 className="font-medium">
                                    {staff.name ||
                                      (staff.firstName && staff.lastName ?
                                        `${staff.firstName} ${staff.lastName}` :
                                        staff.firstName || staff.lastName || staff.username)}
                                  </h2>
                                  <p className="text-[10px] text-gray-400 font-normal tracking-tight">{staff.email}</p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider border ${staff.is_active ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-600 border-red-100'
                                  }`}>
                                  {staff.is_active ? 'Active' : 'Offline'}
                                </span>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => { handleEdit(staff); setShowStaffModal(false); }}
                                    className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center hover:bg-white hover:border-bulky-blue-500 hover:text-bulky-blue-500 transition-all"
                                  >
                                    <span className="material-icons-round text-xs">edit</span>
                                  </button>
                                  <button
                                    onClick={() => { handleToggleStatus(staff.id, staff.is_active); setShowStaffModal(false); }}
                                    className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center hover:bg-white hover:border-red-500 hover:text-red-500 transition-all"
                                  >
                                    <span className="material-icons-round text-xs">{staff.is_active ? 'block' : 'check_circle'}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="material-icons-round text-gray-200 text-4xl block mb-2">group_off</span>
                    <p className="text-xs font-normal text-gray-400 uppercase tracking-wider">No staff members found</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button onClick={() => setShowStaffModal(false)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Delete Confirmation Dialog */}
      {
        deleteConfirm.show && createPortal(
          <div className="modal-overlay">
            <div className="modal-container max-w-sm">
              <div className="modal-header bg-red-600 from-red-600 to-red-700">
                <div className="flex items-center gap-2">
                  <span className="material-icons-round text-white text-sm">warning</span>
                  <h2 className="font-medium">Confirm Delete</h2>
                </div>
                <button onClick={() => setDeleteConfirm({ show: false, userId: null, userName: '' })} className="text-white hover:bg-white/20 p-1 rounded">
                  <span className="material-icons-round text-sm">close</span>
                </button>
              </div>
              <div className="modal-content text-center py-6">
                <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteConfirm.userName}</strong>?</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-2">This operation is irreversible</p>
              </div>
              <div className="modal-footer">
                <button onClick={() => setDeleteConfirm({ show: false, userId: null, userName: '' })} className="btn-secondary">Cancel</button>
                <button 
                  onClick={confirmDelete} 
                  disabled={isDeleting}
                  className={`btn-danger ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isDeleting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Deleting...</span>
                    </div>
                  ) : (
                    'Delete User'
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Status Toggle Dialog */}
      {
        statusToggle.show && createPortal(
          <div className="modal-overlay">
            <div className="modal-container max-w-sm">
              <div className={`modal-header ${statusToggle.currentStatus ? 'bg-orange-600 from-orange-600 to-orange-700' : 'bg-green-600 from-green-600 to-green-700'}`}>
                <div className="flex items-center gap-2">
                  <span className="material-icons-round text-white text-sm">{statusToggle.currentStatus ? 'block' : 'check_circle'}</span>
                  <h2 className="font-medium">{statusToggle.currentStatus ? 'Deactivate' : 'Activate'} User</h2>
                </div>
                <button onClick={() => setStatusToggle({ show: false, userId: null, currentStatus: false, userName: '', notes: '' })} className="text-white hover:bg-white/20 p-1 rounded">
                  <span className="material-icons-round text-sm">close</span>
                </button>
              </div>
              <div className="modal-content space-y-4">
                <p className="text-xs font-normal text-gray-600 uppercase tracking-wider text-center mt-2">Changing status for {statusToggle.userName}</p>
                <div>
                  <label className="form-label">Notes (Optional)</label>
                  <textarea
                    value={statusToggle.notes}
                    onChange={(e) => setStatusToggle(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-input min-h-[80px] resize-none"
                    placeholder="Enter reason for status change..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setStatusToggle({ show: false, userId: null, currentStatus: false, userName: '', notes: '' })} className="btn-secondary">Cancel</button>
                <button onClick={confirmStatusToggle} className={statusToggle.currentStatus ? 'btn-danger' : 'btn-primary'}>Confirm</button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Comprehensive User Edit Form */}
      {showEditForm && editingUser && createPortal(
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                  <span className="material-icons-round text-2xl">fingerprint</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight leading-none">Edit User</h2>
                  <p className="text-bulky-navy-300 text-[11px] font-semibold tracking-wider mt-1.5 opacity-80 uppercase">Editing user: {editingUser.username}</p>
                </div>
              </div>
              <button
                onClick={handleEditFormCancel}
                className="w-11 h-11 hover:bg-white/10 text-white rounded-2xl flex items-center justify-center transition-all bg-white/5 border border-white/5 shadow-lg"
              >
                <span className="material-icons-round text-2xl">close</span>
              </button>
            </div>

            <form onSubmit={handleEditFormSubmit} className="flex flex-col overflow-hidden">
              <div className="modal-content">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Account Hub */}
                  <div className="lg:col-span-3 form-section">
                    <h3 className="form-section-title">
                      <span className="material-icons-round text-bulky-blue-500 text-lg">admin_panel_settings</span>
                      Account Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="space-y-2">
                        <label className="form-label">Username</label>
                        <input type="text" value={editFormData.username} onChange={(e) => handleEditFormChange('username', e.target.value)} className="form-input" required />
                      </div>
                      <div className="space-y-2">
                        <label className="form-label">Email Address</label>
                        <input type="email" value={editFormData.email} onChange={(e) => handleEditFormChange('email', e.target.value)} className="form-input" required />
                      </div>
                      <div className="space-y-2">
                        <label className="form-label">Phone Number (Locked)</label>
                        <input type="text" value={editFormData.mobile} disabled className="form-input bg-gray-50/50 cursor-not-allowed text-gray-400 font-mono" />
                      </div>
                      <div className="space-y-2 relative">
                        <label className="form-label">User Role</label>
                        <select value={editFormData.role} onChange={(e) => handleEditFormChange('role', e.target.value)} className="form-select" required>
                          <option value="user_staff">Staff User</option>
                          <option value="user_admin">Admin User</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                        <span className="material-icons-round absolute right-4 top-[42px] text-gray-400 pointer-events-none text-lg">expand_more</span>
                      </div>
                    </div>
                  </div>

                  {/* Personal Module */}
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <span className="material-icons-round text-emerald-500 text-lg">portrait</span>
                      Personal Information
                    </h3>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="form-label">First Name</label>
                        <input type="text" value={editFormData.firstName} onChange={(e) => handleEditFormChange('firstName', e.target.value)} className="form-input" />
                      </div>
                      <div className="space-y-2">
                        <label className="form-label">Last Name</label>
                        <input type="text" value={editFormData.lastName} onChange={(e) => handleEditFormChange('lastName', e.target.value)} className="form-input" />
                      </div>
                    </div>
                  </div>

                  {/* Geo-Spatial Module */}
                  <div className="lg:col-span-2 form-section">
                    <h3 className="form-section-title">
                      <span className="material-icons-round text-bulky-blue-500 text-lg">map</span>
                      Address Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                      <div className="lg:col-span-3 space-y-2">
                        <label className="form-label">Address Line 1</label>
                        <input type="text" value={editFormData.address_line_1} onChange={(e) => handleEditFormChange('address_line_1', e.target.value)} className="form-input" />
                      </div>
                      <div className="lg:col-span-2 space-y-2">
                        <label className="form-label">ZIP Code</label>
                        <input type="text" value={editFormData.zip_code} onChange={(e) => handleEditFormChange('zip_code', e.target.value)} className="form-input" />
                      </div>
                      <div className="lg:col-span-2 space-y-2 relative">
                        <label className="form-label">State</label>
                        <select value={editFormData.state} onChange={(e) => handleEditFormChange('state', e.target.value)} className="form-select">
                          <option value="">Select State</option>
                          {states.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                        </select>
                        <span className="material-icons-round absolute right-4 top-[42px] text-gray-400 pointer-events-none text-lg">expand_more</span>
                      </div>
                      <div className="lg:col-span-3 space-y-2 relative">
                        <label className="form-label">City</label>
                        <select value={editFormData.city} onChange={(e) => handleEditFormChange('city', e.target.value)} className="form-select" disabled={!editFormData.state}>
                          <option value="">Select City</option>
                          {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                        </select>
                        <span className="material-icons-round absolute right-4 top-[42px] text-gray-400 pointer-events-none text-lg">expand_more</span>
                      </div>
                      <div className="lg:col-span-5 space-y-2">
                        <label className="form-label">Notes</label>
                        <input type="text" value={editFormData.notes} onChange={(e) => handleEditFormChange('notes', e.target.value)} className="form-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleEditFormCancel} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Staff Creation Modal */}
      {
        showStaffCreation && (
          <StaffCreation
            onClose={() => setShowStaffCreation(false)}
            onStaffCreated={handleStaffCreated}
          />
        )
      }

      {/* Package Info Modal */}
      {
        showPackageInfoModal && createPortal(
          <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md z-[999999] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-white/20 animate-in zoom-in duration-300">
              {/* Header */}
              <div className="bg-bulky-navy-800 p-6 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-white">
                    <span className="material-icons-round text-sm">inventory_2</span>
                  </div>
                  <div>
                    <h2 className="font-semibold text-white text-lg">Subscription Details</h2>
                    <p className="text-[10px] font-medium text-white/90 uppercase tracking-wider mt-1">Resource allocation and capacity metrics</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPackageInfoModal(false)}
                  className="w-8 h-8 hover:bg-white/10 text-white rounded-lg flex items-center justify-center transition-all"
                >
                  <span className="material-icons-round text-base">close</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
                {(() => {
                  const subscription = getCurrentSubscription();
                  const staffLimits = getStaffLimits();

                  if (!subscription) {
                    return (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-orange-100">
                          <span className="material-icons-round text-orange-500 text-2xl">warning_amber</span>
                        </div>
                        <h2 className="font-medium">No Active Plan</h2>
                        <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider leading-relaxed max-w-[240px] mx-auto">No active subscription detected. Please upgrade to access features.</p>
                      </div>
                    );
                  }

                  const pkg = subscription.package;
                  return (
                    <div className="space-y-6">
                      {/* Package Overview */}
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/5 rounded-full blur-2xl -mr-16 -mt-16"></div>
                        <div className="relative z-10 flex items-center justify-between mb-6">
                          <div>
                            <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider mb-1 block">Current Plan</span>
                            <h2 className="font-medium">{pkg.name}</h2>
                          </div>
                          <div className="text-right">
                            <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider mb-1 block">Price</span>
                            <div className="text-lg font-normal text-bulky-blue-600 tabular-nums">₹{pkg.price}</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white border border-gray-100 rounded-lg p-3">
                            <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider block mb-1">Plan Type</span>
                            <div className="text-[10px] font-normal text-bulky-navy-700 uppercase tracking-wider">{pkg.package_type}</div>
                          </div>
                          <div className="bg-white border border-gray-100 rounded-lg p-3">
                            <span className="text-[9px] font-medium text-gray-600 uppercase tracking-wider block mb-1">Status</span>
                            <div className="text-[10px] font-normal text-emerald-500 uppercase tracking-wider">ACTIVE</div>
                          </div>
                        </div>
                      </div>

                      {/* Staff Management Limits */}
                      <div className="space-y-4">
                        <h2 className="font-medium">
                          <span className="material-icons-round text-blue-500 text-sm">analytics</span>
                          Usage Statistics
                        </h2>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                            <div className="text-sm font-normal text-bulky-navy-800 tabular-nums">{pkg.max_staff_members}</div>
                            <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">Capacity</div>
                          </div>
                          <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                            <div className="text-sm font-normal text-blue-600 tabular-nums">{staffLimits.current}</div>
                            <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">In Use</div>
                          </div>
                          <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                            <div className="text-sm font-normal text-green-600 tabular-nums">{pkg.max_staff_members - staffLimits.current}</div>
                            <div className="text-[9px] font-medium text-gray-600 uppercase tracking-wider">Available</div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="space-y-1.5 pt-2">
                          <div className="flex justify-between text-[8px] font-normal text-gray-400 uppercase tracking-wider">
                            <span>Usage Level</span>
                            <span>{Math.round((staffLimits.current / pkg.max_staff_members) * 100)}%</span>
                          </div>
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-bulky-blue-500 rounded-full transition-all duration-500"
                              style={{ width: `${(staffLimits.current / pkg.max_staff_members) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Expiry Info */}
                      <div className="bg-bulky-navy-900 rounded-xl p-5 shadow-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                            <span className="material-icons-round text-white text-sm">event</span>
                          </div>
                          <div>
                            <span className="text-[8px] font-normal text-bulky-navy-300 uppercase tracking-wider mb-1 block">Expires On</span>
                            <div className="font-normal text-white text-[11px] uppercase tracking-wider">
                              {(() => {
                                const date = subscription.expires_at.includes('T') || subscription.expires_at.includes('-') 
                                  ? new Date(subscription.expires_at)
                                  : new Date(parseInt(subscription.expires_at));
                                return date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                });
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Other package details */}
                      <div className="bg-white rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-icons-round text-orange-600 text-sm">template_add</span>
                          <span className="font-medium text-gray-800">Templates</span>
                        </div>
                        <div className="text-xl font-semibold text-orange-600">{pkg.max_templates || 0}</div>
                      </div>

                      <div className="bg-white rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-icons-round text-red-600 text-sm">send</span>
                          <span className="font-medium text-gray-800">Daily Messages</span>
                        </div>
                        <div className="text-xl font-semibold text-red-600">{(pkg.max_bulk_messages_per_day || 0).toLocaleString()}</div>
                      </div>

                      <div className="bg-white rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-icons-round text-teal-600 text-sm">schedule</span>
                          <span className="font-medium text-gray-800">Duration</span>
                        </div>
                        <div className="text-xl font-semibold text-teal-600">{pkg.duration_days || 0} days</div>
                      </div>

                      {/* Subscription Timeline */}
                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h2 className="font-medium">
                          <span className="material-icons-round text-gray-600">schedule</span>
                          Subscription Timeline
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white rounded-lg p-4">
                            <div className="text-sm text-gray-600">Started On</div>
                            <div className="font-normal text-gray-800">
                              {(() => {
                                const date = subscription.starts_at.includes('T') || subscription.starts_at.includes('-') 
                                  ? new Date(subscription.starts_at)
                                  : new Date(parseInt(subscription.starts_at));
                                return date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                });
                              })()}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4">
                            <div className="text-sm text-gray-600">Expires On</div>
                            <div className="font-normal text-gray-800">
                              {(() => {
                                const date = subscription.expires_at.includes('T') || subscription.expires_at.includes('-') 
                                  ? new Date(subscription.expires_at)
                                  : new Date(parseInt(subscription.expires_at));
                                return date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                });
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-50 p-4 bg-gray-50/50">
                <div className="flex justify-between items-center gap-4">
                  <div className="text-[8px] font-normal text-gray-400 uppercase tracking-wider max-w-[200px] leading-relaxed">
                    Request resource expansion via command center support.
                  </div>
                  <button
                    onClick={() => setShowPackageInfoModal(false)}
                    className="px-5 py-2 bg-bulky-navy-900 text-white rounded-lg text-[9px] font-normal uppercase tracking-wider hover:bg-bulky-navy-800 transition-all active:scale-95"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Subscription Details Modal */}
      <SubscriptionDetails
        isOpen={showSubscriptionDetails}
        onClose={() => {
          setShowSubscriptionDetails(false);
          setSelectedSubscription(null);
        }}
        subscription={selectedSubscription}
      />

      {/* Error Modal */}
      {showErrorModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="material-icons-round text-red-600 text-lg">error</span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Cannot Delete User</h2>
                  <p className="text-sm text-gray-600">Action blocked</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {errorMessage}
              </p>
              
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    setErrorMessage('');
                  }}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {showSuccessModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto transform animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="material-icons-round text-green-600 text-lg">check_circle</span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">User Deleted Successfully</h2>
                  <p className="text-sm text-gray-600">Operation completed</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {successMessage}
              </p>
              
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage('');
                  }}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Great!
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserManagement;